import { OrderByOptions } from '@/enums/order-by';
import { DetailedGameRound } from '@/models/detailed-round';
import { GenericError } from '@/models/generic-error';
import { GameRoundFilterQuery } from '@/services/dto/game-rounds/game-rounds-filter-query.dto';
import {
  useInfiniteQueryCallbacks,
  useInfiniteQueryReset,
} from '@starsoft/common/hooks';
import { PaginatedResponse } from '@starsoft/common/models';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { GameRoundQueryKeys } from '../../queryKeys';
import { UseGameRoundsProps, UseGameRoundsQueryKey } from './props';
import { fetchGameRounds } from './service';

export function useGameRounds(options?: UseGameRoundsProps) {
  const { gameId, limit = 20 } = options ?? {};

  const filter: GameRoundFilterQuery = {
    gameId: gameId as bigint,
    limit,
    sortDirection: OrderByOptions.Descending,
  };

  const queryKey: UseGameRoundsQueryKey = [GameRoundQueryKeys.Primary, filter];

  useInfiniteQueryReset(queryKey);
  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();

  const { data, fetchStatus, ...infinityQuery } = useInfiniteQuery<
    PaginatedResponse<DetailedGameRound>,
    AxiosError<GenericError>,
    InfiniteData<PaginatedResponse<DetailedGameRound>>,
    UseGameRoundsQueryKey
  >({
    queryKey,
    queryFn: fetchGameRounds,
    enabled: !!gameId,
    getNextPageParam,
    getPreviousPageParam,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const rounds = useMemo(() => {
    return data?.pages.flatMap(page => page?.data) ?? [];
  }, [data]);

  return {
    ...infinityQuery,
    rounds,
    isLoading: infinityQuery.isPending && fetchStatus !== 'idle',
    error: infinityQuery.error as AxiosError<GenericError>,
  };
}
